/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// import WDYU from 'why-did-you-update';

// Needed for redux-saga es6 generator support
import 'babel-polyfill';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';
import ReactGA from 'react-ga';
import _ from 'lodash';
import ttiPolyfill from 'tti-polyfill';
import { ENV_SETTINGS } from 'shared/constants/index';

import 'sanitize.css/sanitize.css';
// import { authAction } from 'containers/Auth/actions';
import ErrorBoundary from 'containers/ErrorBoundary';

// Import root app
import App from 'containers/App';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';

// Load the favicon, the manifest.json file and the .htaccess file
/* eslint-disable import/no-unresolved, import/extensions */
import '!file-loader?name=[name].[ext]!./images/favicon.ico';
import '!file-loader?name=[name].[ext]!./images/icon-72x72.png';
import '!file-loader?name=[name].[ext]!./images/icon-96x96.png';
import '!file-loader?name=[name].[ext]!./images/icon-128x128.png';
import '!file-loader?name=[name].[ext]!./images/icon-144x144.png';
import '!file-loader?name=[name].[ext]!./images/icon-152x152.png';
import '!file-loader?name=[name].[ext]!./images/icon-192x192.png';
import '!file-loader?name=[name].[ext]!./images/icon-384x384.png';
import '!file-loader?name=[name].[ext]!./images/icon-512x512.png';
import '!file-loader?name=[name].[ext]!./manifest.json';
// import '!file-loader?name=[name].[ext]!./shared/iframe/questionario/index.html';
import 'file-loader?name=[name].[ext]!./.htaccess';
/* eslint-enable import/no-unresolved, import/extensions */

import { store, history } from './configureStore';

// Import i18n messages
import { translationMessages } from './i18n';

// Import CSS reset and Global Styles
import './global-styles';

const MOUNT_NODE = document.getElementById('app');

// Initialize google analytics page view tracking
ReactGA.initialize(ENV_SETTINGS.ga[process.env.NODE_ENV], {
  titleCase: false,
});

history.listen((location) => {
  ReactGA.set({ page: _.trim(location.pathname, '/') });
  ReactGA.pageview(_.trim(location.pathname, '/'));
});

ttiPolyfill.getFirstConsistentlyInteractive().then((tti) => {
  ReactGA.timing({
    category: 'Load Performace',
    variable: 'Time to Interactive',
    value: Math.round(tti),
  });
});

const callback = (list) => {
  list.getEntries().forEach((entry) => {
    if (entry.name.includes('App')) {
      ReactGA.timing({
        category: 'App Render Performace',
        variable: entry.entryType,
        value: Math.round(entry.startTime),
      });
    }
  });
};

const observer = new window.PerformanceObserver(callback);
observer.observe({
  entryTypes: [
    'navigation',
    'paint',
    'resource',
    'mark',
    'measure',
  ],
});

// window.onbeforeunload = () => localStorage.removeItem('session');

// Init Auth Action
/*
const sessionString = localStorage.getItem('session');
if (sessionString !== null) {
  store.dispatch(authAction(JSON.parse(sessionString)));
}
*/

const render = (messages) => {
  ReactDOM.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <ConnectedRouter history={history}>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </ConnectedRouter>
      </LanguageProvider>
    </Provider>,
    MOUNT_NODE
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./i18n', 'containers/App'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  (new Promise((resolve) => {
    resolve(import('intl'));
  }))
    .then(() => Promise.all([
      import('intl/locale-data/jsonp/it.js'),
    ]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
/* if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'test') {
  const OfflinePluginRuntime = require('offline-plugin/runtime'); // eslint-disable-line global-require
  OfflinePluginRuntime.install({
    // Tells to new SW to take control immediately
    onUpdateReady: () => OfflinePluginRuntime.applyUpdate(),
    // Tells the UpdateLink component to reload into the new version
    onUpdated: () => {
      window.swUpdate = true;
    },
  });
}*/

if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'test') {
  // require('offline-plugin/runtime').install(); // eslint-disable-line global-require
  const runtime = require('offline-plugin/runtime'); // eslint-disable-line global-require
  // console.log('installing Offline require');
  runtime.install({
    onInstalled: () => {
      console.log('SW Event:', 'Installed');
    },
    onUpdating: () => {
      console.log('SW Event:', 'onUpdating');
    },
    onUpdateReady: () => {
      console.log('SW Event:', 'onUpdateReady');
      // Tells to new SW to take control immediately
      runtime.applyUpdate();
    },
    onUpdated: () => {
      console.log('SW Event:', 'onUpdated');
      // Reload the webpage to load into the new version
      window.location.reload();
    },
    onUpdateFailed: () => {
      console.log('SW Event:', 'onUpdateFailed');
    },
  });
}

// Why did you update debug
/* if (process.env.NODE_ENV !== 'production') {
  WDYU(React);
} */




// WEBPACK FOOTER //
// ./app/app.js